import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, MessageSquare, Sparkles } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";

/**
 * "Ask the transcript" panel. Renders on the Minutes page once a transcript
 * exists. Users type a question → Claude answers grounded strictly in the
 * transcript. All Q&A are persisted server-side so re-opening the session
 * shows the interrogation history.
 *
 * Props:
 *   - meetingId: string
 *   - initialQa: array of {id, question, answer, asked_at} (from meeting.transcript_qa)
 */
export default function AskTranscript({ meetingId, initialQa }) {
  const [qa, setQa] = useState(Array.isArray(initialQa) ? initialQa : []);
  const [question, setQuestion] = useState("");
  const [pending, setPending] = useState(false);

  // If the parent refetches the meeting (e.g. after transcribe-and-redraft),
  // rehydrate qa with the fresh server-side history WITHOUT clobbering any
  // locally-appended entries that haven't been reflected server-side yet.
  useEffect(() => {
    if (!Array.isArray(initialQa)) return;
    setQa((prev) => {
      const known = new Set(prev.map((e) => e.id));
      const merged = [...prev];
      for (const entry of initialQa) {
        if (!known.has(entry.id)) merged.push(entry);
      }
      // Preserve chronological order (server orders by insertion).
      return merged.sort((a, b) => (a.asked_at || "").localeCompare(b.asked_at || ""));
    });
  }, [initialQa]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const q = question.trim();
    if (!q || pending) return;
    setPending(true);
    try {
      const res = await api.post(`/meetings/${meetingId}/ask`, { question: q });
      setQa((prev) => [...prev, res.data]);
      setQuestion("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 404) {
        toast.error("Session no longer available.");
      } else if (detail && typeof detail === "object") {
        if (detail.code === "no_transcript") {
          toast.error("Transcribe the recording first.");
        } else if (detail.code === "ai_upstream_unavailable") {
          toast.error("AI is temporarily unavailable. Try again in a moment.");
        } else if (detail.code === "question_too_long") {
          toast.error("Keep questions under 500 characters.");
        } else if (detail.code === "rate_limited") {
          toast.error(detail.message || "Too many questions on this session. Try again later.");
        } else {
          toast.error(detail.message || "Ask failed");
        }
      } else {
        toast.error("Ask failed");
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="surface rounded-md p-6" data-testid="ask-transcript-block">
      <div className="flex items-center gap-2">
        <Sparkles size={16} className="text-[#c89f45]" />
        <p className="label-eyebrow">Interrogate the record</p>
      </div>
      <h3 className="font-serif text-xl mt-2">Ask the transcript</h3>
      <p className="text-sm text-[#888] mt-2 max-w-2xl">
        Every word on the record. Ask what you committed to, what you dodged, what your partner
        pushed back on. Answers are grounded strictly in what was said.
      </p>

      {qa.length > 0 && (
        <ul className="mt-6 space-y-5" data-testid="ask-transcript-history">
          {qa.map((entry) => (
            <li key={entry.id} className="border-l-2 border-[#c89f45]/40 pl-4" data-testid={`ask-entry-${entry.id}`}>
              <p className="text-sm text-[#ddd] font-medium flex items-start gap-2">
                <MessageSquare size={12} className="text-[#c89f45] mt-1 shrink-0" />
                <span>{entry.question}</span>
              </p>
              <p className="text-sm text-[#bbb] mt-2 leading-relaxed whitespace-pre-wrap" data-testid={`ask-answer-${entry.id}`}>
                {entry.answer}
              </p>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="mt-6 flex items-center gap-2" data-testid="ask-transcript-form">
        <div className="flex-1 relative">
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            maxLength={500}
            placeholder="What did I commit to this week?"
            disabled={pending}
            className="w-full h-11 bg-[#121212] border border-[#2a2a2a] focus:border-[#c89f45] outline-none rounded-md px-4 pr-16 text-sm text-[#fafafa] placeholder:text-[#666] transition-colors"
            data-testid="ask-transcript-input"
          />
          {question.length > 0 && (
            <span
              className={`absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-mono ${
                question.length >= 480 ? "text-[#e07b6a]" : "text-[#666]"
              }`}
              data-testid="ask-transcript-charcount"
            >
              {question.length}/500
            </span>
          )}
        </div>
        <Button
          type="submit"
          disabled={pending || !question.trim()}
          className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] font-medium rounded-md h-11 px-4"
          data-testid="ask-transcript-submit"
        >
          <Send size={14} className="mr-1.5" />
          {pending ? "Asking…" : "Ask"}
        </Button>
      </form>
    </div>
  );
}
